import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useWorkflowDefinitions } from '@/hooks'
import { TRIGGER_ON_LABELS, TRIGGER_ON_VALUES, type ChainInputModel, type ChainLinkModel, type ChainModel, type TriggerOn } from '@/types'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { useMemo } from 'react'

const INPUT_TYPES = ['text', 'textarea', 'guid', 'number', 'boolean', 'json', 'context-picker', 'testsuite-picker', 'connector-picker', 'file-upload']

interface ChainBuilderProps {
  projectId: string
  model: ChainModel
  onChange: (model: ChainModel) => void
  readOnly?: boolean
}

export function ChainBuilder({ projectId, model, onChange, readOnly = false }: ChainBuilderProps) {
  const { data } = useWorkflowDefinitions(projectId)
  const workflowOptions = useMemo(
    () => (data?.items ?? []).filter((w) => w.isActive).map((w) => ({ name: w.name, displayName: w.displayName })),
    [data],
  )

  const setInputs = (inputs: ChainInputModel[]) => onChange({ ...model, inputs })
  const setLinks = (links: ChainLinkModel[]) => onChange({ ...model, links })

  const updateInput = (idx: number, patch: Partial<ChainInputModel>) =>
    setInputs(model.inputs.map((inp, i) => (i === idx ? { ...inp, ...patch } : inp)))
  const addInput = () => setInputs([...model.inputs, { name: '', type: 'text', required: true }])
  const removeInput = (idx: number) => setInputs(model.inputs.filter((_, i) => i !== idx))

  const updateLink = (idx: number, patch: Partial<ChainLinkModel>) =>
    setLinks(model.links.map((l, i) => (i === idx ? { ...l, ...patch } : l)))
  const addLink = () =>
    setLinks([...model.links, { name: `Link ${model.links.length + 1}`, workflow: workflowOptions[0]?.name ?? '', triggerOn: 'success', inputs: [] }])
  const removeLink = (idx: number) => setLinks(model.links.filter((_, i) => i !== idx))
  const moveLink = (idx: number, dir: -1 | 1) => {
    const j = idx + dir
    if (j < 0 || j >= model.links.length) return
    const next = [...model.links]
    ;[next[idx], next[j]] = [next[j], next[idx]]
    setLinks(next)
  }

  const setLinkInputEntries = (idx: number, entries: [string, string][]) =>
    updateLink(idx, { inputs: entries })

  return (
    <div className="space-y-6" data-testid="chain-builder">
      <section className="rounded-lg border p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-bold text-card-foreground">Chain start inputs</Label>
          <Button variant="outline" size="sm" onClick={addInput} disabled={readOnly}>
            <Plus className="h-4 w-4" /> Add input
          </Button>
        </div>
        {model.inputs.length === 0 && <p className="text-sm text-muted-foreground">No start inputs.</p>}
        {model.inputs.map((inp, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input className="flex-1" data-testid={`chain-input-name-${i}`} placeholder="name" value={inp.name} onChange={(e) => updateInput(i, { name: e.target.value })} disabled={readOnly} />
            <Select value={inp.type} onValueChange={(v) => updateInput(i, { type: v })} disabled={readOnly}>
              <SelectTrigger className="w-44" aria-label="Input type" data-testid={`chain-input-type-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {INPUT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                {inp.type && !INPUT_TYPES.includes(inp.type) && (
                  <SelectItem value={inp.type}>{inp.type}</SelectItem>
                )}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-1.5 text-xs whitespace-nowrap">
              <Checkbox checked={inp.required ?? false} onCheckedChange={(v) => updateInput(i, { required: v === true })} disabled={readOnly} />
              required
            </label>
            <Button variant="ghost" size="sm" onClick={() => removeInput(i)} aria-label="Remove input" disabled={readOnly}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-bold text-card-foreground">Links</Label>
          <Button variant="outline" size="sm" onClick={addLink} disabled={readOnly}>
            <Plus className="h-4 w-4" /> Add link
          </Button>
        </div>
        {model.links.length === 0 && <p className="text-sm text-muted-foreground">No links yet. Add the first link.</p>}
        {model.links.map((link, i) => {
          const entries = Array.isArray(link.inputs) ? link.inputs : []
          return (
            <div key={i} className="rounded-lg border p-4 space-y-3" data-testid={`chain-link-${i}`}>
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground shrink-0">{i + 1}</span>
                <Input className="flex-1" data-testid={`chain-link-name-${i}`} placeholder="Link name" value={link.name} onChange={(e) => updateLink(i, { name: e.target.value })} disabled={readOnly} />
                <Button variant="ghost" size="sm" onClick={() => moveLink(i, -1)} disabled={readOnly || i === 0} aria-label="Move up"><ArrowUp className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => moveLink(i, 1)} disabled={readOnly || i === model.links.length - 1} aria-label="Move down"><ArrowDown className="h-4 w-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => removeLink(i)} aria-label="Remove link" disabled={readOnly}><Trash2 className="h-4 w-4" /></Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Workflow</Label>
                  <Select value={link.workflow} onValueChange={(v) => updateLink(i, { workflow: v })} disabled={readOnly}>
                    <SelectTrigger aria-label="Workflow" data-testid={`chain-link-workflow-${i}`}><SelectValue placeholder="Select workflow…" /></SelectTrigger>
                    <SelectContent>
                      {workflowOptions.map((w) => <SelectItem key={w.name} value={w.name}>{w.displayName}</SelectItem>)}
                      {link.workflow && !workflowOptions.some((w) => w.name === link.workflow) && (
                        <SelectItem value={link.workflow}>{link.workflow}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Trigger on</Label>
                  <Select value={link.triggerOn} onValueChange={(v) => updateLink(i, { triggerOn: v as TriggerOn })} disabled={readOnly}>
                    <SelectTrigger aria-label="Trigger on" data-testid={`chain-link-trigger-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TRIGGER_ON_VALUES.map((t) => <SelectItem key={t} value={t}>{TRIGGER_ON_LABELS[t]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Inputs (key → expression / literal)</Label>
                  <Button variant="ghost" size="sm" onClick={() => setLinkInputEntries(i, [...entries, ['', '']])} disabled={readOnly}>
                    <Plus className="h-4 w-4" /> Add
                  </Button>
                </div>
                {entries.length === 0 && <p className="text-xs text-muted-foreground">No inputs mapped.</p>}
                {entries.map(([k, v], ei) => (
                  <div key={ei} className="flex items-center gap-2">
                    <Input
                      className="w-40"
                      data-testid={`chain-link-${i}-input-key-${ei}`}
                      placeholder="key"
                      value={k}
                      disabled={readOnly}
                      onChange={(e) => {
                        const next = [...entries]
                        next[ei] = [e.target.value, v]
                        setLinkInputEntries(i, next)
                      }}
                    />
                    <span className="text-muted-foreground">=</span>
                    <Input
                      className="flex-1 font-mono text-xs"
                      data-testid={`chain-link-${i}-input-value-${ei}`}
                      placeholder="${{ chain.inputs.workItemId }}"
                      value={v}
                      disabled={readOnly}
                      onChange={(e) => {
                        const next = [...entries]
                        next[ei] = [k, e.target.value]
                        setLinkInputEntries(i, next)
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setLinkInputEntries(i, entries.filter((_, x) => x !== ei))}
                      aria-label="Remove input"
                      disabled={readOnly}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </section>
    </div>
  )
}
